// =====================================================================
// src/components/intl-acca/FixtureRow.tsx
// =====================================================================
// Single fixture row — used inside the Tournament Hub calendar and the
// Home page's "today's fixtures" rail. Pure presentational, takes an
// IntlAccaMatch + nation lookup from props.

import type { IntlAccaMatch, Nation } from "../../db/intl-acca-types-extended";
import { NationFlag } from "./NationFlag";

interface FixtureRowProps {
  match: IntlAccaMatch;
  /** Lookup map: nation code → Nation. Host-supplied. */
  nations: Record<string, Nation>;
  /** Click handler — host wires to /intl-acca/slip/:slipId when a slip exists for this match. */
  onSelect?: () => void;
  /** Optional pill on the right — e.g. "FOCUS" when a slip has been published for this match. */
  rightPill?: React.ReactNode;
}

export function FixtureRow({
  match,
  nations,
  onSelect,
  rightPill,
}: FixtureRowProps): JSX.Element {
  const home = match.home ? (nations[match.home] ?? null) : null;
  const away = match.away ? (nations[match.away] ?? null) : null;
  // Unresolved knockout slot → show the published descriptor.
  const homeLabel = home?.name ?? match.home ?? match.home_slot ?? "TBD";
  const awayLabel = away?.name ?? match.away ?? match.away_slot ?? "TBD";
  const isLive = match.status === "live";
  const isDone = match.status === "finished";

  const stageLabel =
    match.knockout_round ?? (match.group_id ? `GROUP ${match.group_id}` : "—");

  return (
    <div className="ia-fixture-row" onClick={onSelect}>
      <span className="ia-fixture-row-label">{stageLabel}</span>
      <span className="ia-fixture-row-label">
        {match.kickoff
          ? new Date(match.kickoff).toLocaleDateString("en-GB", { month: "short", day: "numeric" })
          : "TBD"}
      </span>

      <div className="ia-fixture-row-teams">
        <NationFlag nation={home} size={26} />
        <span style={{ fontSize: "var(--sz-body-sm)", color: "var(--text)" }}>
          {homeLabel}
        </span>
        <span className="ia-fixture-row-vs">vs</span>
        <span style={{ fontSize: "var(--sz-body-sm)", color: "var(--text)" }}>
          {awayLabel}
        </span>
        <NationFlag nation={away} size={26} />
      </div>

      {/* Score / kickoff */}
      <div style={{ textAlign: "center" }}>
        {isLive && match.score ? (
          <div>
            <div className="ia-fixture-row-score" style={{ color: "var(--pos)" }}>
              {match.score}
            </div>
            <div
              className="ia-mono"
              style={{
                fontSize: "var(--sz-mono-xs)",
                color: "var(--pos)",
                marginTop: 2,
                letterSpacing: "0.18em",
              }}
            >
              {match.minute}' LIVE
            </div>
          </div>
        ) : isDone && match.score ? (
          <div>
            <div className="ia-fixture-row-score" style={{ color: "var(--text-secondary)" }}>
              {match.score}
            </div>
            <div
              className="ia-mono"
              style={{
                fontSize: "var(--sz-mono-xs)",
                color: "var(--text-tertiary)",
                marginTop: 2,
                letterSpacing: "0.18em",
              }}
            >
              FT
            </div>
          </div>
        ) : match.kickoff ? (
          <div className="ia-fixture-row-kickoff">
            {new Date(match.kickoff).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
          </div>
        ) : (
          <span className="ia-mono" style={{ color: "var(--text-tertiary)" }}>
            TBD
          </span>
        )}
      </div>

      <div style={{ textAlign: "right" }}>{rightPill}</div>
    </div>
  );
}
